// F3: object list — every entity on the drawing, grouped by layer in z-order (top first).
// Click = select on canvas · × = delete · drag = reorder within the layer (z-order command).
import { useState } from "preact/hooks";
import type { AppApi } from "../appApi";
import type { ToolManager } from "../tools/toolManager";
import type { Doc, Entity } from "../doc/entities";
import { makeDeleteCommand } from "../doc/commands";
import { formatLength } from "../core/units";
import { Icon, type IconName } from "./icons";

function iconFor(e: Entity): IconName {
  switch (e.type) {
    case "polyline":
      return e.closed ? "rect" : "line";
    case "circle":
      return "circle";
    case "arc":
      return "arc";
    case "text":
      return "text";
    case "label":
      return "label";
    case "dimension":
      return "dimension";
    case "image":
      return "image";
    case "grid":
      return "grid";
    case "marker":
      return e.kind === "cloud" ? "cloud" : "highlighter";
    case "group":
      return "group";
  }
}

function describeEntity(e: Entity, doc: Doc): string {
  const excerpt = (s: string) => (s.length > 18 ? `${s.slice(0, 18)}…` : s) || "(empty)";
  switch (e.type) {
    case "polyline":
      return `${e.closed ? "Shape" : "Line"} · ${e.points.length} pts`;
    case "circle":
      return `Circle · r ${formatLength(e.r, doc.units, doc.precision)}`;
    case "arc":
      return "Arc";
    case "text":
      return `Text · ${excerpt(e.content)}`;
    case "label":
      return `Label · ${excerpt(e.content)}`;
    case "dimension":
      return e.kind === "linear" ? "Dimension" : `Dimension · ${e.kind}`;
    case "image":
      return `Image · ${Math.round(e.wMm)}×${Math.round(e.hMm)}mm`;
    case "grid":
      return `Grid · ${e.cols}×${e.rows}`;
    case "marker":
      return e.kind === "cloud" ? "Revision cloud" : "Highlight";
    case "group": {
      // 3.20.0: a wall run says what it is
      const w = e.meta?.wall as { thickness?: number; doors?: unknown[]; closed?: boolean } | undefined;
      if (w && typeof w.thickness === "number") {
        const n = Array.isArray(w.doors) ? w.doors.length : 0;
        return `${w.closed ? "Room" : "Wall"} · ${Math.round(w.thickness)} mm${n ? ` · ${n} ${n === 1 ? "door" : "doors"}` : ""}`;
      }
      return `Group · ${e.children.length} objects`;
    }
  }
}

export function ObjectsPanel({ api, tools }: { api: AppApi; tools: ToolManager }) {
  const { store } = api;
  const doc = store.doc;
  const [dragId, setDragId] = useState<string | null>(null);

  const onDrop = (targetId: string, layerId: string) => {
    if (!dragId || dragId === targetId) return;
    const entity = doc.entities[dragId];
    const list = doc.zOrder[layerId] ?? [];
    if (!entity || entity.layerId !== layerId || !list.includes(dragId)) {
      setDragId(null);
      return; // same-layer reorder only (move layers via right-click → Move to layer)
    }
    store.dispatch({
      kind: "reorder",
      moves: [
        {
          id: dragId,
          from: { layerId, index: list.indexOf(dragId) },
          to: { layerId, index: list.indexOf(targetId) }
        }
      ]
    });
    setDragId(null);
  };

  const layers = [...doc.layers].reverse(); // topmost layer first, matching the Layers panel
  const total = Object.keys(doc.entities).length;
  if (total === 0) return <small class="muted">Nothing on the drawing yet.</small>;

  return (
    <div class="objects-panel">
      {layers.map((layer) => {
        const ids = [...(doc.zOrder[layer.id] ?? [])].reverse(); // top-most drawn first
        if (ids.length === 0) return null;
        return (
          <div key={layer.id} class="objects-layer">
            <div class="objects-layer-name">{layer.name}</div>
            {ids.map((id) => {
              const e = doc.entities[id];
              if (!e) return null;
              const selected = tools.selection.has(id);
              return (
                <div
                  key={id}
                  class={`object-row${selected ? " selected" : ""}${e.hidden ? " dimmed" : ""}`}
                  draggable
                  onDragStart={() => setDragId(id)}
                  onDragOver={(ev) => ev.preventDefault()}
                  onDrop={() => onDrop(id, layer.id)}
                  onClick={(ev) => {
                    if (ev.shiftKey) tools.selection.toggle(id);
                    else tools.selection.set([id]);
                  }}
                  title="Click = select · Shift+click = add · drag = reorder within layer"
                >
                  <span class="object-icon">
                    <Icon name={iconFor(e)} size={13} />
                  </span>
                  <span class="object-name">{describeEntity(e, doc)}</span>
                  {e.locked && <span class="object-flag" title="Locked">🔒</span>}
                  <button
                    class="object-delete"
                    title="Delete object"
                    aria-label="Delete object"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      store.dispatch(makeDeleteCommand(doc, [id]));
                    }}
                  >
                    <Icon name="close" size={11} />
                  </button>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
